/**
 * Complaint Filtering API Utility
 *
 * Provides a centralized interface for calling the backend hybrid filter endpoint
 * Handles pagination, authorization, error handling, and response normalization
 *
 * Hybrid Architecture:
 * - Tier 1 (Backend SQL): Status, Severity, Device IDs, Departments, Duration filters
 * - Tier 2 (Backend Calculations): Date-based filters (complaint age, resolution time)
 * - Tier 3 (Backend Text Search): Complaint ID, title, details, device name, reporter name
 * - Tier 4 (Frontend): Pagination, UI state
 *
 * Usage:
 *   const { complaints, pagination } = await fetchFilteredComplaints(filters, pagination, token);
 */

import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';

/**
 * Fetch filtered complaints from backend with pagination support
 *
 * @param {Object} filters - Filter state object with structure:
 *   {
 *     statuses: ['open', 'in_progress'],
 *     severities: ['critical', 'major'],
 *     deviceIds: [],
 *     departments: [],
 *     createdDateFrom: '2025-01-01',
 *     createdDateTo: '2025-01-31',
 *     resolutionDateFrom: '',
 *     resolutionDateTo: '',
 *     daysOpenFrom: '',
 *     daysOpenTo: '',
 *     search: ''
 *   }
 *
 * @param {Object} pagination - Pagination parameters:
 *   {
 *     page: 1,
 *     per_page: 10
 *   }
 *
 * @param {string} token - JWT authentication token
 *
 * @returns {Promise<Object>} Response object:
 *   {
 *     success: true,
 *     complaints: [...],
 *     pagination: {
 *       page: 1,
 *       per_page: 10,
 *       total_count: 156,
 *       total_pages: 16,
 *       has_next: true,
 *       has_previous: false
 *     },
 *     summary: {
 *       open_count: 45,
 *       in_progress_count: 23,
 *       closed_count: 78,
 *       cancelled_count: 10
 *     }
 *   }
 *
 * @throws {Error} If request fails or token is invalid
 */
export const fetchFilteredComplaints = async (filters = {}, pagination = { page: 1, per_page: 10 }, token, sorting = null) => {
  try {
    // Validate inputs
    if (!token) {
      throw new Error('Authentication token is required');
    }

    if (!filters || typeof filters !== 'object') {
      throw new Error('Invalid filters object');
    }

    // Normalize filter object
    const normalizedFilters = normalizeComplaintFilters(filters);

    // Build request body
    const requestBody = {
      pagination: {
        page: Math.max(1, pagination.page || 1),
        per_page: Math.min(Math.max(1, pagination.per_page || 10), 100), // Min 1, Max 100
      },
      filters: normalizedFilters,
    };

    // Add sorting parameters if provided
    if (sorting && sorting.sort_by) {
      requestBody.sorting = {
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction === 'desc' ? 'desc' : 'asc',
      };
    }

    // Make API call
    const response = await axios.post(
      API_ENDPOINTS.COMPLAINTS.FILTER,
      requestBody,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 second timeout
      }
    );

    // Validate response structure
    if (!response.data || !response.data.data) {
      throw new Error('Invalid response structure from server');
    }

    const { success, data, error } = response.data;

    if (!success) {
      throw new Error(error || 'Failed to fetch filtered complaints');
    }

    // Return normalized response
    return {
      success: true,
      complaints: data.complaints || [],
      pagination: data.pagination || {
        page: 1,
        per_page: 10,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_previous: false,
      },
      summary: data.summary || {
        open_count: 0,
        acknowledged_count: 0,
        in_progress_count: 0,
        escalated_to_vendor_count: 0,
        closed_count: 0,
        cancelled_count: 0,
      },
    };
  } catch (error) {
    // Handle different error types
    let errorMessage = 'Failed to fetch filtered complaints';
    let errorCode = 'UNKNOWN_ERROR';

    if (error.response) {
      // Backend returned an error response
      errorCode = error.response.status;

      if (error.response.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        errorCode = 'UNAUTHORIZED';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to view complaints.';
        errorCode = 'FORBIDDEN';
      } else if (error.response.status === 400) {
        errorMessage = error.response.data?.error || 'Invalid filter parameters';
        errorCode = 'BAD_REQUEST';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
        errorCode = 'SERVER_ERROR';
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'Network error. Please check your connection.';
      errorCode = 'NETWORK_ERROR';
    } else if (error.message) {
      // Error in request setup
      errorMessage = error.message;
      errorCode = 'REQUEST_ERROR';
    }

    // Throw error with structured info
    const structuredError = new Error(errorMessage);
    structuredError.code = errorCode;
    structuredError.status = error.response?.status;
    structuredError.originalError = error;

    console.error('[complaintFilterApi.js] Error fetching filtered complaints:', {
      message: errorMessage,
      code: errorCode,
      status: error.response?.status,
      url: error.config?.url,
      originalError: error.message,
    });

    throw structuredError;
  }
};

/**
 * Normalize complaint filter object by removing empty/null values
 *
 * @param {Object} filters - Raw filter object from UI
 * @returns {Object} Normalized filter object
 */
export function normalizeComplaintFilters(filters) {
  const normalized = {};

  // Copy filter properties, removing empty arrays and null values
  Object.keys(filters).forEach((key) => {
    const value = filters[key];

    // Skip null/undefined values
    if (value === null || value === undefined) {
      normalized[key] = value;
      return;
    }

    // Handle arrays - only include non-empty
    if (Array.isArray(value)) {
      normalized[key] = value.length > 0 ? value : [];
      return;
    }

    // Handle empty strings - keep as empty string for search field, null for others
    if (typeof value === 'string' && value.trim() === '') {
      normalized[key] = key === 'search' ? '' : null;
      return;
    }

    // Include all other values
    normalized[key] = value;
  });

  return normalized;
}

/**
 * Clear all filters - returns default filter state
 *
 * @returns {Object} Default filter object
 */
export const getDefaultComplaintFilters = () => ({
  statuses: [],
  severities: [],
  locations: [],
  deviceIds: [],
  departments: [],
  categoryIds: [],
  createdDateFrom: '',
  createdDateTo: '',
  resolutionDateFrom: '',
  resolutionDateTo: '',
  daysOpenFrom: '',
  daysOpenTo: '',
  search: '',
  impactsAvailability: '',
  filedByMe: false,
});

/**
 * Check if any filters are currently active
 *
 * @param {Object} filters - Filter object to check
 * @returns {boolean} True if any non-default filters are set
 */
export const hasActiveComplaintFilters = (filters) => {
  const defaults = getDefaultComplaintFilters();

  return Object.keys(filters).some((key) => {
    // Free-text search is not an Active Filter (cleared via search box only)
    if (key === 'search') return false;

    const filterValue = filters[key];
    const defaultValue = defaults[key];

    if (Array.isArray(filterValue)) {
      return filterValue.length > 0;
    }

    return filterValue !== defaultValue;
  });
};

/**
 * Format pagination for API call
 * Ensures valid page/per_page values
 *
 * @param {number} page - Current page (1-based)
 * @param {number} perPage - Items per page
 * @returns {Object} Validated pagination object
 */
export const formatComplaintPaginationForApi = (page = 1, perPage = 10) => ({
  page: Math.max(1, parseInt(page, 10) || 1),
  per_page: Math.min(Math.max(1, parseInt(perPage, 10) || 10), 100),
});

export default {
  fetchFilteredComplaints,
  getDefaultComplaintFilters,
  hasActiveComplaintFilters,
  formatComplaintPaginationForApi,
};
